import { GridBaseDataProvider } from '../../../grid/GridBaseDataProvider';
import {
  ColumnDetails,
  GetRowsRequest,
  GetSummaryResponse,
  SetFiltersRequest,
  UpdateSettingsRequest,
} from '../../../grid/types/common';
import { Column, ColumnKeys, GetMonitoredRulesResponse, GetRowsResponse, PhasrMonitoredRulesMethod } from '../types';
import { API_TYPE_PHASR } from '../../../webcomponents.constants';
import { ConsoleRequest, DataProviderType } from '../../../utils/request';
import { DEFAULT_PAGE_NUMBER, DEFAULT_PAGE_SIZE } from '../../../grid/gridView/types';
import { saveViewState } from '../../../utils/states.utils';
import { Filter } from '../../../grid/gridFilters/types';
import { convertGridFiltersToExtJsFilters } from '../../../grid/utils/filters';

export class PhasrMonitoredRulesGridDataProvider extends GridBaseDataProvider<Column, ColumnKeys> {
  public companyFilterDataProvider = API_TYPE_PHASR;
  private endpoint = 'MonitoredRules';
  private cachedGetMonitoredRulesResponse?: GetMonitoredRulesResponse;
  private readonly allColumnDetails: {};
  public readonly viewStateName = 'phasr-monitoredRulesViewState';
  private dictionary: any;

  constructor(public dependencies: any, allColumnDetails: ColumnDetails<Column, ColumnKeys>[]) {
    super();
    this.allColumnDetails = allColumnDetails.reduce(
      (acc, curr) => ({
        ...acc,
        [curr.key]: curr,
      }),
      {}
    );
  }

  async getSummary(request: GetRowsRequest<Column, ColumnKeys>): Promise<GetSummaryResponse> {
    this.cachedGetMonitoredRulesResponse = await this.getRules(request);
    if (!this.cachedGetMonitoredRulesResponse) {
      this.cachedGetMonitoredRulesResponse = await this.getRules(request);
    }

    return {
      summary: '',
      numItems: this.cachedGetMonitoredRulesResponse.total,
    };
  }

  async getRows(request: GetRowsRequest<Column, ColumnKeys>): Promise<GetRowsResponse> {
    if (!this.cachedGetMonitoredRulesResponse) {
      this.cachedGetMonitoredRulesResponse = await this.getRules(request);
    }

    return {
      success: true,
      rawEvents: this.cachedGetMonitoredRulesResponse.data,
    };
  }

  async setFilters(request: SetFiltersRequest<ColumnKeys>): Promise<{} | undefined> {
    saveViewState(request, this.viewStateName);
    return;
  }

  async updateSettings(request: UpdateSettingsRequest<Column>): Promise<{}> {
    saveViewState(request, this.viewStateName);
    return {};
  }

  shouldDataReloadOnResetView(): boolean {
    return true;
  }

  private async getRules(request: GetRowsRequest<Column, ColumnKeys>): Promise<GetMonitoredRulesResponse> {
    const convertedFiltersToGzBackend = this.getConvertedFrontendFiltersToGzBackendFilters(request.filters);

    const payload = {
        page: request.pageNumber ?? DEFAULT_PAGE_NUMBER,
        limit: request.pageSize ?? DEFAULT_PAGE_SIZE,
        sort: request.sortColumnName ?? null,
        dir: request.sortDirection ?? null,
        filter: convertGridFiltersToExtJsFilters(convertedFiltersToGzBackend),
      };

    return await ConsoleRequest.createRequest<GetMonitoredRulesResponse>({
        dataProviderType: DataProviderType.CORE,
        payload,
        endpoint: this.endpoint,
        method: PhasrMonitoredRulesMethod.READ_RECORDS,
      });
  }

  private getConvertedFrontendFiltersToGzBackendFilters(filters: Filter<ColumnKeys>[]): Filter<ColumnKeys>[] {
    const convertedFiltersToGzBackend: Filter<ColumnKeys>[] = [];

    if (!this.dictionary) {
      this.dictionary = Application.Main.getPhasrDictionary();
    }

    for (const filter of filters) {
      if (!filter.hasOwnProperty(ColumnKeys.RULE_NAME) &&
          !filter.hasOwnProperty(ColumnKeys.BEHAVIORAL_PROFILE_IDENTITIES) &&
          !filter.hasOwnProperty(ColumnKeys.BEHAVIORAL_PROFILE_RESOURCES)) {
        convertedFiltersToGzBackend.push(filter);
        continue;
      }

      if (filter[ColumnKeys.RULE_NAME]?.complexEnumFilter) {
        const searchedRules = filter[ColumnKeys.RULE_NAME].complexEnumFilter;

        // drop label and leave only array of values
        const searchedRuleKeys = searchedRules.map(rule => (Number)(rule.value));

        convertedFiltersToGzBackend.push({
          [ColumnKeys.RULE_NAME]: {in: searchedRuleKeys},
        });
      }

      if (filter[ColumnKeys.BEHAVIORAL_PROFILE_IDENTITIES]?.complexEnumFilter) {
        const searchedBehavioralProfilesIdentities = filter[ColumnKeys.BEHAVIORAL_PROFILE_IDENTITIES].complexEnumFilter;

        // drop label and leave only array of values
        const searchedBehavioralProfileKeys = searchedBehavioralProfilesIdentities.map(profile => (String)(profile.value));
        convertedFiltersToGzBackend.push({
          [ColumnKeys.BEHAVIORAL_PROFILE_IDENTITIES]: {in: searchedBehavioralProfileKeys},
        });
      }
      if (filter[ColumnKeys.BEHAVIORAL_PROFILE_RESOURCES]?.complexEnumFilter) {
        const searchedBehavioralProfileResources = filter[ColumnKeys.BEHAVIORAL_PROFILE_RESOURCES].complexEnumFilter;

        // drop label and leave only array of values
        const searchedBehavioralProfileResourceKeys = searchedBehavioralProfileResources.map(resource => (String)(resource.value));

        convertedFiltersToGzBackend.push({
          [ColumnKeys.BEHAVIORAL_PROFILE_RESOURCES]: {in: searchedBehavioralProfileResourceKeys},
        });
      }
    }

    return convertedFiltersToGzBackend;
  }
}
